import { useCallback, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';

/** Calls a Cloud Function and tracks busy/error/saved state for inline UI feedback. */
export function useCallable() {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);

  /**
   * `options` is passed straight to httpsCallable. The one that matters is `timeout`: the web
   * SDK gives up after 70 seconds regardless of how long the function itself is allowed to run,
   * so a long job needs it raised on BOTH sides or the browser reports a failure while the
   * import is still working — which looks exactly like the import not working.
   */
  const run = useCallback(async (name, payload, options) => {
    setBusy(true);
    setError('');
    setSaved(false);
    try {
      const result = await httpsCallable(functions, name, options)(payload);
      setSaved(true);
      return result.data;
    } catch (err) {
      setError(err.message || String(err));
      return null;
    } finally {
      setBusy(false);
    }
  }, []);

  return { run, busy, error, saved };
}

export function CallStatus({ busy, error, saved }) {
  if (busy) return <span className="text-xs text-slate-500 dark:text-slate-400">Saving…</span>;
  if (error) return <span className="text-xs text-red-600 dark:text-red-400">{error}</span>;
  if (saved) return <span className="text-xs text-emerald-600 dark:text-emerald-400">Saved</span>;
  return null;
}
